import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_URL from '../api/baseURL';
import { Page, Panel, Button, Table, Tr, Td, EmptyState, Loading, ErrorNote } from '../components/ui/Iparc';
import { badgesFor, oilStatus, hasIssue } from '../hooks/useFleetHealth';
import { IconCar, IconPlus } from '../components/icons/Icons';
import { fmt } from '../theme/iparc';

const VehicleList = ({ token }) => {
  const [vehicles, setVehicles] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | issue | ok
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_URL}/vehicles/mine`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setVehicles(Array.isArray(res.data) ? res.data : []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.response?.data?.message || 'Erreur de chargement des véhicules');
        setLoading(false);
      });
  }, [token]);

  const usable = useMemo(
    () => vehicles.filter((v) => v.plateNumber && (v.brand || v.make) && v.model),
    [vehicles]
  );
  const issueCount = useMemo(() => usable.filter(hasIssue).length, [usable]);

  const shown = useMemo(() => {
    if (filter === 'issue') return usable.filter(hasIssue);
    if (filter === 'ok') return usable.filter((v) => !hasIssue(v));
    return usable;
  }, [usable, filter]);

  const chips = [
    { key: 'all', label: `Tous · ${usable.length}` },
    { key: 'issue', label: `En défaut · ${issueCount}` },
    { key: 'ok', label: `Conformes · ${usable.length - issueCount}` },
  ];

  return (
    <Page
      title="Mes véhicules"
      subtitle="Flotte"
      actions={
        <Button variant="primary" onClick={() => navigate('/add-vehicle')}>
          <IconPlus className="w-3.5 h-3.5" /> Ajouter un véhicule
        </Button>
      }
    >
      {error && <ErrorNote>{error}</ErrorNote>}

      <Panel
        title="Flotte"
        bodyClass=""
        action={
          <div className="flex gap-[5px]">
            {chips.map((c) => (
              <button
                key={c.key}
                onClick={() => setFilter(c.key)}
                className={`h-[26px] px-2.5 rounded-[7px] text-[11px] font-medium transition-colors ${
                  filter === c.key
                    ? 'bg-[#241F1B] text-[#F4F1EA]'
                    : 'text-[#6E665C] hover:text-[#C9C3B6]'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        }
      >
        {loading ? (
          <Loading label="Chargement des véhicules…" />
        ) : usable.length === 0 ? (
          <EmptyState
            title="Aucun véhicule enregistré"
            hint="Ajoutez un véhicule pour suivre ses échéances, son entretien et ses coûts."
            action={
              <Button variant="primary" onClick={() => navigate('/add-vehicle')}>
                Ajouter mon premier véhicule
              </Button>
            }
          />
        ) : shown.length === 0 ? (
          <EmptyState title="Aucun véhicule dans cette vue." />
        ) : (
          <Table head={['Véhicule', 'Kilométrage', 'Prochaine vidange', 'État', '']} minWidth={780}>
            {shown.map((v) => {
              const oil = oilStatus(v);
              return (
                <Tr key={v._id} onClick={() => navigate(`/vehicles/${v._id}`)}>
                  <Td>
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="w-7 h-7 rounded-lg bg-[#241F1B] text-[#A79E90] flex items-center justify-center shrink-0">
                        <IconCar className="w-[15px] h-[15px]" />
                      </span>
                      <div className="min-w-0">
                        <p className="font-['Space_Grotesk',sans-serif] text-[13px] font-semibold tracking-[.02em] text-[#F4F1EA] whitespace-nowrap">
                          {v.plateNumber}
                        </p>
                        <p className="text-[11px] text-[#6E665C] truncate">
                          {[v.brand || v.make, v.model].filter(Boolean).join(' ')}
                        </p>
                      </div>
                    </div>
                  </Td>
                  <Td mono>
                    {v.odometerReading != null ? (
                      <>
                        {fmt(v.odometerReading)} <span className="text-[10.5px] text-[#6E665C]">km</span>
                      </>
                    ) : (
                      <span className="text-[#5C554C]">—</span>
                    )}
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2 min-w-[130px]">
                      <span className="flex-1 h-[5px] rounded-[3px] bg-[#241F1B] overflow-hidden">
                        <span
                          className="block h-full rounded-[3px]"
                          style={{ width: oil.pct, background: oil.color }}
                        />
                      </span>
                      <span className="text-[10.5px] text-[#8F877B] whitespace-nowrap">{oil.label}</span>
                    </div>
                  </Td>
                  <Td>
                    <div className="flex flex-wrap gap-1.5">
                      {badgesFor(v).map((b) => (
                        <span
                          key={b.text}
                          className="px-2 py-[2px] rounded-full text-[10px] font-semibold whitespace-nowrap"
                          style={{ background: b.tint, color: b.color }}
                        >
                          {b.text}
                        </span>
                      ))}
                    </div>
                  </Td>
                  <Td className="text-right">
                    <Button
                      variant="quiet"
                      className="h-[26px] px-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/vehicle/${v._id}/history`);
                      }}
                    >
                      Historique
                    </Button>
                  </Td>
                </Tr>
              );
            })}
          </Table>
        )}
      </Panel>
    </Page>
  );
};

export default VehicleList;
